import type { EventMarketDetail } from '../api/types';

/** Markets shown before the "additional markets" fold, when any are active. */
export const MAX_DEFAULT_SHOWN_ACTIVE = 6;
/** Markets shown before the fold when the whole event has resolved. */
export const MAX_DEFAULT_SHOWN_CLOSED = 3;

export type MarketComparator = (
    a: EventMarketDetail,
    b: EventMarketDetail
) => number;

// Ranking uses the API's volume only; rows may show a candle-derived fallback instead.
export const byVolumeDesc: MarketComparator = (a, b) =>
    (b.volume ?? 0) - (a.volume ?? 0);

export const byProbabilityDesc: MarketComparator = (a, b) =>
    (b.current_yes_prob ?? 0) - (a.current_yes_prob ?? 0);

/** Sort rank keeping resolved markets below live ones. */
export const closedRank = (m: EventMarketDetail): number =>
    m.closed === true ? 1 : 0;

export interface MarketSplit {
    shownMarkets: EventMarketDetail[];
    hiddenMarkets: EventMarketDetail[];
}

/** Rank by `compare`, active before closed, and fold the tail away. */
export function splitMarkets(
    markets: EventMarketDetail[],
    compare: MarketComparator = byVolumeDesc
): MarketSplit {
    const active = markets.filter(m => m.closed !== true).sort(compare);
    const closed = markets.filter(m => m.closed === true).sort(compare);

    if (active.length === 0) {
        return {
            shownMarkets: closed.slice(0, MAX_DEFAULT_SHOWN_CLOSED),
            hiddenMarkets: closed.slice(MAX_DEFAULT_SHOWN_CLOSED),
        };
    }

    const ranked = [...active, ...closed];
    return {
        shownMarkets: ranked.slice(0, MAX_DEFAULT_SHOWN_ACTIVE),
        hiddenMarkets: ranked.slice(MAX_DEFAULT_SHOWN_ACTIVE),
    };
}
